<template>
  <div>
    <custom-dasboard
      ref="customDashboard"
      :is-mobile="isMobile"
      :sections="sections"
    />
    <immediate-message-popup
      id="immediateMessagePopup"
      ref="_immediateMessagePopupRef"
      :items="immediateMessages"
      @message-marked-as-read="markMessageAsRead"
    />
  </div>
</template>
<script>
import mixAlert from '@quisit/mipe-utils/mixins/alert.mixin';
import CustomDasboard from 'custom-dashboard';
import ImmediateMessagePopup from '@/components/common/messages/ImmediateMessagePopup.vue';

import sessionManager from '@/utils/managers/sessionManager';
import sessionKeys from '@/utils/constants/sessionKeys.js';
import messageCodes from '@/utils/constants/messageLocationCode.js';

import _service from '@/services/mipe/dashboard';
import moment from 'moment';

import APPS from '@/utils/constants/applications';
import TYPES from '@/utils/constants/scheduleItemTypes';

export default {
  name: 'Dashboard',
  components: {
    CustomDasboard,
    ImmediateMessagePopup,
  },
  mixins: [mixAlert],
  data() {
    return {
      immediateMessages: [],
      sections: [],
    };
  },
  computed: {
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    enrollmentDynamicIndicators() {
      return [
        {
          text: 'Cantidad de Contratos Firmados:',
          color: '#9b4dca',
          bg: '#f3e8ff',
          icon: 'fas fa-file-signature',
          dataKey: 'totalSignedContract',
          format: 'number',
        },
      ];
    },
  },
  async mounted() {
    await Promise.all([this.getImmediateMessages(), this.getSections()]);
    if (this.isMobile) {
      const options = this.getOptions(TYPES.Alerts);
      if (options && options.pagination) {
        options.pagination.pageSize = 1;
      }
    }
  },
  methods: {
    async getSections() {
      const payload = { applicationId: APPS.RemPro };
      const { data } = await _service.getSections(payload);
      data.forEach((x) => {
        switch (x.id) {
          case TYPES.News:
            x.options = {
              data: [],
              load: this.getNews,
            };
            break;
          case TYPES.Event:
            x.options = {
              loading: false,
              pagination: {
                data: [],
                count: 0,
                totalItemCount: 0,
                currentPage: 0,
                pageCount: 1,
                pageSize: 4,
              },
              selectedDate: new Date(),
              routeToViewAll: 'calendarMain',
              load: this.getEvents,
            };
            break;
          case TYPES.Workshop:
            x.options = {
              count: null,
              data: [],
              currentPage: 1,
              pageCount: null,
              totalItemCount: null,
              routeTo: 'workshop',
              routeToViewAll: 'workshop',
              load: this.getWorkShops,
            };
            break;
          case TYPES.Message:
            x.options = {
              data: 0,
              routeToViewAll: 'dashboardMaintenance',
              load: this.getMessages,
            };
            break;
          case TYPES.TeachingMaterial:
            x.options = {
              count: null,
              data: [],
              currentPage: 1,
              pageCount: null,
              totalItemCount: null,
              routeTo: 'dashboardMaintenance',
              routeToViewAll: 'dashboardMaintenance',
              load: this.getTeachingMaterial,
            };
            break;
          case TYPES.EducationalMaterial:
            x.options = {
              count: 0,
              currentPage: 1,
              data: [],
              pageCount: 0,
              totalItemCount: 0,
              categories: [],
              load: this.getEducationalMaterials,
            };
            break;
          case TYPES.Graphic:
            x.options = {
              data: [],
              load: this.getGraphics,
              getDetails: this.getGraphicDetails,
            };
            break;
          case TYPES.Metrics:
            x.isDynamic = true;
            x.options = {
              data: { totalSignedContract: 0 },
              indicators: this.enrollmentDynamicIndicators,
              load: this.getMetrics,
            };
            break;
          case TYPES.Alerts:
            x.options = {
              pagination: {
                data: [],
                count: 0,
                totalItemCount: 0,
                currentPage: 0,
                pageCount: 1,
                pageSize: 4,
              },
              load: this.getAlerts,
            };
            break;
          default:
            x.options = {};
            break;
        }
        x.options = { ...x.options, appId: APPS.RemPro.toLowerCase() };
      });
      this.sections = data;
    },
    async fetchSectionsData() {
      const loads = this.sections
        .filter((x) => x.options.load)
        .map((x) => x.options.load());
      await Promise.all(loads);
    },
    getOptions(id) {
      const section = this.sections.find((x) => x.id === id);
      return section?.options ?? {};
    },
    async getMessages() {
      const options = this.getOptions(TYPES.Message);
      const payload = { applicationId: APPS.RemPro };
      const { data } = await _service.fetchUnreadMessages(payload);
      options.data = data;
    },
    async getNews() {
      const options = this.getOptions(TYPES.News);
      const payload = { applicationId: APPS.RemPro };
      const { data } = await _service.getNews(payload);
      options.data = data;
    },
    async getEvents(currentPage = 1) {
      const options = this.getOptions(TYPES.Event);
      if (options.loading) return;
      const { pagination, selectedDate } = options;
      const { pageSize } = pagination;
      const date = moment(selectedDate)
        .startOf('day')
        .toDate();
      const payload = {
        date,
        currentPage,
        pageSize,
      };
      options.loading = true;
      const { data } = await _service.getEvents(payload);
      options.loading = false;
      const values =
        currentPage > 1 ? pagination.data.concat(...data.data) : data.data;

      options.pagination = { ...data, data: values, pageSize };
    },
    async getWorkShops(currentPage = 1) {
      const options = this.getOptions(TYPES.Workshop);
      const payload = {
        currentPage,
        pageSize: 5,
        isFromDashboard: true,
        applicationId: APPS.RemPro,
      };
      const { data } = await _service.getWorkshops(payload);
      options.data = [...options.data, ...data.data];
      options.currentPage = data.currentPage;
      options.count = data.count;
      options.pageCount = data.pageCount;
      options.totalItemCount = data.totalItemCount;
    },
    async getTeachingMaterial(currentPage = 1) {
      const options = this.getOptions(TYPES.TeachingMaterial);
      const payload = {
        currentPage,
        pageSize: 5,
        isFromDashboard: true,
        applicationId: APPS.RemPro,
      };
      const { data } = await _service.getTeachingMaterial(payload);
      options.data = [...options.data, ...data.data];
      options.currentPage = data.currentPage;
      options.count = data.count;
      options.pageCount = data.pageCount;
      options.totalItemCount = data.totalItemCount;
    },
    async getAlerts(currentPage = 1) {
      const options = this.getOptions(TYPES.Alerts);
      const { pagination } = options;
      const { pageSize } = pagination;
      const payload = { currentPage, pageSize, applicationId: APPS.RemPro };
      const { data } = await _service.getAlerts(payload);
      options.pagination = { ...data, pageSize };
    },
    async getEducationalMaterials(currentPage = 1, categoryId = null) {
      const options = this.getOptions(TYPES.EducationalMaterial);
      const payload = {
        currentPage,
        pageSize: 3,
        applicationId: APPS.RemPro,
      };
      if (categoryId) payload.categoryId = categoryId;
      const { data } = await _service.getEducationalMaterials(payload);
      const values =
        currentPage > 1 ? options.data.concat(...data.data) : data.data;
      options.data = values;
      options.currentPage = data.currentPage;
      options.count = data.count;
      options.pageCount = data.pageCount;
      options.totalItemCount = data.totalItemCount;
    },
    async getEducationalMaterialCategories() {
      const options = this.getOptions(TYPES.EducationalMaterial);
      const { data } = await _service.getEducationalMaterialCategories();
      options.categories = data;
    },
    async markMessageAsRead(id) {
      await _service.saveReadMessage(id);
    },
    async getImmediateMessages() {
      const loaded = sessionManager.get(sessionKeys.KEY_MESSAGES_LOADED, false);
      if (loaded) {
        this.immediateMessages = sessionManager.get(
          sessionKeys.KEY_IMMEDIATE_MESSAGES,
          '[]'
        );
      } else {
        const { data } = await _service.fetchMessages();
        const messages = data.filter((s) =>
          [messageCodes.Inicio, messageCodes.InicioConAcuse].includes(
            s.messageLocationCode
          )
        );
        sessionManager.set(sessionKeys.KEY_IMMEDIATE_MESSAGES, messages);
        sessionManager.set(sessionKeys.KEY_MESSAGES_LOADED, true);
        this.immediateMessages = messages;
      }

      this.$nextTick(() => {
        if (this.immediateMessages.length > 0)
          this.$refs._immediateMessagePopupRef.init();
      });
    },
    async getGraphics() {
      const options = this.getOptions(TYPES.Graphic);
      const payload = { applicationId: APPS.RemPro };
      const { data } = await _service.getGraphics(payload);
      options.data = data.map((x) => ({
        ...x,
        active: false,
        detail: [],
      }));
    },
    async getGraphicDetails(reportId) {
      const { data } = await _service.getGraphicByReportType(reportId);
      return data;
    },
    async getMetrics() {
      const options = this.getOptions(TYPES.Metrics);
      const { data } = await _service.getEnrollmentCount();
      options.data = data;
    },
  },
};
</script>
